import * as React from "react";
import TicketCard from "../components/TicketCard";
import TicketRegistrationForm from "../components/TicketRegistrationForm";
import UserQRCode from "../components/UserQRCode";
import { useAuth } from "../auth/OidcAuthProvider";
import { fetchTicketsByCell, fetchTicketsByEmail, fetchUserTickets, TicketSummary } from "../utils/TicketService";
import { useNavigate } from "react-router-dom";
import { Attendee, isProfileComplete, UpdateProfile, useUser } from "@/utils/AccountStore";
import { getOidcConfig } from "@/utils/oidcConfig";
import { GetMetadata } from "@/utils/MetadataStore";
import { FaEdit, FaEnvelope, FaHome, FaPhone, FaQrcode, FaSyncAlt, FaTicketAlt, FaTimes, FaUser } from "react-icons/fa";

const Tickets: React.FC = () => {
    const config = getOidcConfig();

    const { isAuthenticated, isLoading: authLoading, signIn, signOut, user, accessToken, error, clearError, userData } = useAuth();

    const [tickets, setTickets] = React.useState<TicketSummary[]>([]);
    const [ticketError, setTicketError] = React.useState<string | null>(null);
    const [loadingTickets, setLoadingTickets] = React.useState(false);
    const [currentEventId, setCurrentEventId] = React.useState<string | null | undefined>(null);

    const [savingProfile, setSavingProfile] = React.useState(false);
    const [showQRCode, setShowQRCode] = React.useState(false);
    const [isEditingProfile, setIsEditingProfile] = React.useState(false);

    const { user: profile, getUser } = useUser();
    const profileComplete = isProfileComplete(profile);

    const navigate = useNavigate();

    React.useEffect(() => {
        let active = true;
        GetMetadata()
            .then((metadata) => {
                if (!active) return;
                const eventId = metadata.currentEventId?.trim();
                setCurrentEventId(eventId || null);
                if (!eventId) setTicketError("No current event is configured for tickets.");
            })
            .catch(() => {
                if (!active) return;
                setCurrentEventId(null);
                setTicketError("Unable to load the current event for tickets.");
            });
        return () => { active = false; };
    }, []);

    React.useEffect(() => {
        if (userData && user && isAuthenticated) {
            let updated = false;
            let updatedProfile = profile;

            if (!updatedProfile.firstName) {
                updatedProfile = { ...updatedProfile, firstName: userData.firstName || "" };
                updated = true;
            }
            if (!updatedProfile.lastName) {
                updatedProfile = { ...updatedProfile, lastName: userData.lastName || "" };
                updated = true;
            }
            if (!updatedProfile.email) {
                updatedProfile = { ...updatedProfile, email: userData.email || "" };
                updated = true;
            }
            if (!updatedProfile.mobileNumber) {
                updatedProfile = { ...updatedProfile, mobileNumber: userData.phoneNumber || "" };
                updated = true;
            }
            if (updated) {
                UpdateProfile(updatedProfile).then(() => {
                    getUser();
                });
            }
        }
    }, [userData]);

    const loadTickets = React.useCallback(async () => {
        if (!accessToken || !profile || !currentEventId) {
            return;
        }
        setLoadingTickets(true);
        setTicketError(null);
        try {
            const results = await Promise.all([
                fetchUserTickets(accessToken, currentEventId),
                fetchTicketsByCell(profile?.mobileNumber || "", accessToken, currentEventId),
                fetchTicketsByEmail(profile?.email || "", accessToken, currentEventId),
            ]);
            const currentTickets = results
                .flat()
                .filter((ticket) => ticket.campaignId === currentEventId);
            setTickets(Array.from(new Map(currentTickets.map((ticket) => [ticket.id || ticket.voucherKey, ticket])).values()));
        } catch (err) {
            console.error("Failed to load tickets", err);
            setTicketError(err instanceof Error ? err.message : "Unable to load tickets");
        } finally {
            setLoadingTickets(false);
        }
    }, [accessToken, currentEventId, profile]);

    React.useEffect(() => {
        if (isAuthenticated && profileComplete) {
            loadTickets();
        }
    }, [isAuthenticated, profileComplete, loadTickets]);

    const handleRegistrationSubmit = async (attendee: Attendee) => {
        setSavingProfile(true);
        try {
            let updatedProfile = { ...profile, firstName: attendee.firstName, lastName: attendee.lastName, email: attendee.email, mobileNumber: attendee.mobileNumber };

            UpdateProfile(updatedProfile).then(() => {
                fetch(config.authority + "/api/accountactions/UpdateUser", {
                    method: "POST",
                    headers: { Authorization: `IdentityBearer ${accessToken}`, "Content-Type": "application/json" },
                    body: JSON.stringify({
                        id: userData.id,
                        firstName: updatedProfile.firstName,
                        lastName: updatedProfile.lastName,
                        email: updatedProfile.email,
                        phoneNumber: updatedProfile.mobileNumber,
                    }),
                })
                    .then((res) => res.json())
                    .catch(() => {});

                getUser();
            });
            if (isAuthenticated && accessToken) {
                await loadTickets();
            }
        } finally {
            setSavingProfile(false);
        }
    };

    if (authLoading) {
        return (
            <div
                className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
                style={{
                    backgroundImage: "url(/images/collectivebg-2.jpg)",
                }}
            >
                <div className="min-h-screen backdrop-blur-sm flex items-center justify-center" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                    <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center space-y-4" style={{ borderLeft: `4px solid #DB1C7D` }}>
                        <div className="mb-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent mx-auto" style={{ borderTopColor: "#DB1C7D" }}></div>
                        </div>
                        <h1 className="text-xl font-bold" style={{ color: "#013A51" }}>
                            Checking your session
                        </h1>
                        <p className="text-sm" style={{ color: "#708372" }}>
                            Please wait...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div
                className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
                style={{
                    backgroundImage: "url(/images/collectivebg-2.jpg)",
                }}
            >
                <div className="min-h-screen backdrop-blur-sm flex items-center justify-center px-3 sm:px-4" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                    <div
                        className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-4 sm:space-y-6 text-center shadow-lg"
                        style={{ borderLeft: `4px solid #DB1C7D` }}
                    >
                        <div className="mb-4">
                            <FaTicketAlt aria-hidden="true" className="inline-block text-4xl sm:text-6xl" style={{ color: "#DB1C7D" }} />
                        </div>
                        <h1 className="text-xl sm:text-2xl font-bold" style={{ color: "#013A51" }}>
                            Sign in to view tickets
                        </h1>
                        <p className="text-xs sm:text-sm" style={{ color: "#708372" }}>
                            You need to register or sign in with our identity provider before accessing your tickets.
                        </p>
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4" role="alert">
                                <p className="text-xs sm:text-sm text-red-600">{error}</p>
                            </div>
                        )}
                        <button
                            className="w-full sm:w-auto px-6 sm:px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm text-white text-sm sm:text-base"
                            style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}
                            onClick={() => signIn("/tickets")}
                        >
                            Continue with your account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!profileComplete) {
        return (
            <div
                className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
                style={{
                    backgroundImage: "url(/images/collectivebg-2.jpg)",
                }}
            >
                <div className="min-h-screen backdrop-blur-sm px-4 py-10" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                    <div className="max-w-2xl mx-auto space-y-6">
                        <div className="flex items-center justify-between">
                            <button
                                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                                style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                                onClick={() => {
                                    navigate("/");
                                }}
                            >
                                <FaHome aria-hidden="true" className="text-white text-xl" />
                                <span className="text-white font-semibold">Home</span>
                            </button>
                        </div>
                        <TicketRegistrationForm
                            initialProfile={
                                profile ?? {
                                    firstName: "",
                                    lastName: "",
                                    email: "",
                                    mobileNumber: "",
                                }
                            }
                            onSubmit={handleRegistrationSubmit}
                            submitting={savingProfile}
                            error={error}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
            style={{
                backgroundImage: "url(/images/collectivebg-2.jpg)",
            }}
        >
            <div className="min-h-screen backdrop-blur-sm px-2 sm:px-4 py-6 sm:py-10" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
                    {/* User Profile Section */}
                    <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg" style={{ borderLeft: `4px solid #DB1C7D` }}>
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-2xl md:text-3xl" style={{ backgroundColor: "#DB1C7D" }}>
                                    <FaUser aria-hidden="true" className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg md:text-xl font-bold" style={{ color: "#013A51" }}>
                                        {profile?.firstName} {profile?.lastName}
                                    </h2>
                                    <p className="text-xs md:text-sm" style={{ color: "#708372" }}>
                                        Your Profile
                                    </p>
                                </div>
                            </div>
                            <button
                                className="px-3 md:px-4 py-2 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg transform hover:scale-105 text-white text-xs md:text-sm"
                                style={{ backgroundColor: "#DB1C7D" }}
                                onClick={() => setIsEditingProfile(!isEditingProfile)}
                            >
                                {isEditingProfile ? (
                                    <>
                                        <FaTimes aria-hidden="true" className="inline-block mr-1" />
                                        Cancel
                                    </>
                                ) : (
                                    <>
                                        <FaEdit aria-hidden="true" className="inline-block mr-1" />
                                        Update
                                    </>
                                )}
                            </button>
                        </div>

                        {isEditingProfile ? (
                            // Edit Mode
                            <div className="mt-4">
                                <TicketRegistrationForm
                                    initialProfile={profile ?? { firstName: "", lastName: "", email: "", mobileNumber: "" }}
                                    onSubmit={(attendee) => {
                                        handleRegistrationSubmit(attendee).then(() => {
                                            setIsEditingProfile(false);
                                        });
                                    }}
                                    submitting={savingProfile}
                                    error={error}
                                />
                            </div>
                        ) : (
                            // View Mode
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div className="bg-gray-50 rounded-lg p-3 md:p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FaEnvelope aria-hidden="true" style={{ color: "#DB1C7D" }} />
                                        <span className="text-xs font-medium" style={{ color: "#708372" }}>
                                            Email
                                        </span>
                                    </div>
                                    <p className="text-sm md:text-base font-medium" style={{ color: "#013A51" }}>
                                        {profile?.email || "Not provided"}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3 md:p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FaPhone aria-hidden="true" style={{ color: "#DB1C7D" }} />
                                        <span className="text-xs font-medium" style={{ color: "#708372" }}>
                                            Phone
                                        </span>
                                    </div>
                                    <p className="text-sm md:text-base font-medium" style={{ color: "#013A51" }}>
                                        {profile?.mobileNumber || "Not provided"}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Header Section */}
                    <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg" style={{ borderLeft: `4px solid #DB1C7D` }}>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <FaTicketAlt aria-hidden="true" className="text-2xl md:text-3xl" style={{ color: "#DB1C7D" }} />
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold" style={{ color: "#013A51" }}>
                                        My Tickets
                                    </h1>
                                    <p className="text-xs md:text-sm mt-1" style={{ color: "#708372" }}>
                                        View and present the QR codes associated with your account.
                                    </p>
                                </div>
                            </div>

                            {/* Mobile-first button layout */}
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <button
                                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm text-white flex-1 sm:flex-none"
                                    style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}
                                    onClick={() => setShowQRCode(!showQRCode)}
                                >
                                    <FaQrcode aria-hidden="true" className="text-lg" />
                                    <span className="text-sm sm:text-base">{showQRCode ? "Hide QR" : "Show QR"}</span>
                                </button>
                                <button
                                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm text-white flex-1 sm:flex-none"
                                    style={{ backgroundColor: "rgba(219, 28, 125, 0.9)" }}
                                    onClick={loadTickets}
                                    disabled={loadingTickets || !currentEventId}
                                >
                                    <FaSyncAlt aria-hidden="true" className="text-lg" />
                                    <span className="text-sm sm:text-base">{loadingTickets ? "Refreshing..." : "Refresh"}</span>
                                </button>
                                <button
                                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 flex-1 sm:flex-none"
                                    style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                                    onClick={() => navigate("/")}
                                >
                                    <FaHome aria-hidden="true" className="text-white text-lg" />
                                    <span className="text-white text-sm sm:text-base">Home</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {ticketError && (
                        <div className="bg-red-50 bg-opacity-90 backdrop-blur-sm border border-red-200 rounded-2xl p-4 shadow-lg" style={{ borderLeft: `4px solid #DC2626` }}>
                            <p className="text-sm text-red-600">{ticketError}</p>
                        </div>
                    )}

                    {/* QR Code Display */}
                    {showQRCode && user && (
                        <UserQRCode
                            userId={String(user.profile?.sub || user.profile?.preferred_username || user.profile?.email || "unknown")}
                            userName={`${user.profile?.given_name || ""} ${user.profile?.family_name || ""}`.trim() || String(user.profile?.email || "User")}
                            onClose={() => setShowQRCode(false)}
                        />
                    )}

                    {/* Tickets Content */}
                    {currentEventId === null ? null : (currentEventId === undefined || loadingTickets) && tickets.length === 0 ? (
                        <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 sm:p-12 shadow-lg text-center" style={{ borderLeft: `4px solid #DB1C7D` }}>
                            <div className="mb-4">
                                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-transparent mx-auto" style={{ borderTopColor: "#DB1C7D" }}></div>
                            </div>
                            <h3 className="text-lg sm:text-xl font-bold" style={{ color: "#013A51" }}>
                                {currentEventId === undefined ? "Loading current event..." : "Loading your tickets..."}
                            </h3>
                        </div>
                    ) : tickets.length === 0 ? (
                        <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 sm:p-12 shadow-lg text-center" style={{ borderLeft: `4px solid #DB1C7D` }}>
                            <div className="mb-4">
                                <FaTicketAlt aria-hidden="true" className="inline-block text-4xl sm:text-6xl" style={{ color: "#DB1C7D" }} />
                            </div>
                            <h3 className="text-xl sm:text-2xl font-bold mb-2" style={{ color: "#013A51" }}>
                                No Tickets Found
                            </h3>
                            <p className="text-sm sm:text-lg" style={{ color: "#708372" }}>
                                No tickets found for your account.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3 sm:space-y-4">
                            {tickets.map((ticket: TicketSummary) => (
                                <TicketCard key={ticket.id || ticket.voucherKey} ticket={ticket} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Tickets;
